import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { useUserStore } from '@/stores/user';
import { useAppStore } from '@/stores/app';
import { ElMessage } from 'element-plus';

// API响应类型定义
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  code?: number;
  error?: string;
}

// 创建axios实例
const isDev = import.meta.env.DEV;
const baseURL = isDev ? '/api' : (import.meta.env.VITE_API_BASE_URL || '/api');
const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// 请求拦截器
apiClient.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    const userStore = useUserStore();
    const appStore = useAppStore();
    
    // 设置加载状态
    if (config.showLoading !== false) {
      appStore.incrementLoading();
    }
    
    // 添加认证token
    const token = userStore.token;
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // 添加请求ID用于追踪
    config.headers = config.headers || {};
    config.headers['X-Request-ID'] = generateRequestId();
    
    // 记录请求日志
    console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config.params || config.data);
    
    return config;
  },
  (error: AxiosError) => {
    const appStore = useAppStore();
    appStore.decrementLoading();
    
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const appStore = useAppStore();
    appStore.decrementLoading();
    
    // 记录响应日志
    console.log(`[API Response] ${response.config?.url}`, response.data);
    
    const { data } = response;
    
    // 处理自定义响应格式
    if (data.success === false) {
      const errorMessage = data.message || '请求失败';
      ElMessage.error(errorMessage);
      return Promise.reject(new Error(errorMessage));
    }
    
    // 统一返回data字段
    return data.data !== undefined ? data.data : data;
  },
  (error: AxiosError<ApiResponse>) => {
    const appStore = useAppStore();
    appStore.decrementLoading();
    
    const userStore = useUserStore();
    
    // 处理网络错误
    if (!error.response) {
      ElMessage.error('网络连接失败，请检查网络设置');
      return Promise.reject(error);
    }
    
    const { status, data } = error.response;
    
    // 记录错误日志
    console.error('[API Response Error]', error.response);
    
    // 根据状态码处理错误
    switch (status) {
      case 401:
        // Token过期或无效
        ElMessage.error('登录已过期，请重新登录');
        userStore.logout();
        break;
      case 403:
        // 权限不足
        ElMessage.error('权限不足，无法执行此操作');
        break;
      case 404:
        // 资源不存在
        ElMessage.error('请求的资源不存在');
        break;
      case 500:
        // 服务器错误（优先显示后端返回的具体错误信息）
        {
          const msg = data?.message || data?.error || '服务器内部错误，请稍后重试';
          ElMessage.error(msg);
        }
        break;
      default:
        // 其他错误
        const errorMessage = data?.message || `请求失败 (${status})`;
        ElMessage.error(errorMessage);
    }
    
    return Promise.reject(error);
  }
);

// 生成请求ID
function generateRequestId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
}

// API请求方法封装
export const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) => 
    apiClient.get<T, T>(url, config),
  
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => 
    apiClient.post<T, T>(url, data, config),
  
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => 
    apiClient.put<T, T>(url, data, config),
  
  delete: <T = any>(url: string, config?: AxiosRequestConfig) => 
    apiClient.delete<T, T>(url, config),
  
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => 
    apiClient.patch<T, T>(url, data, config)
};

// 导出axios实例以便特殊场景使用
export { apiClient };

export default api;